import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import './CustomTooltip.scss';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'primereact/tooltip';
import { Button } from 'primereact/button';

interface TooltipProps {
  tooltipText: string;
  additionalIcoClass?: string;
  iconClass?: string;
  addMargin?: boolean;
  targetRef?: string | null;
  direction?: any;
  isMobileTooltip?: boolean;
  options?: any;
  link?: string;
  linkLabel?: string;
}

const CustomTooltip: React.FC<TooltipProps> = ({
  tooltipText,
  additionalIcoClass = '',
  iconClass = 'ico--info',
  addMargin = true,
  targetRef = null,
  direction = 'top',
  isMobileTooltip = false,
  link,
  linkLabel,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleLinkClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (link) {
        navigate(link);
      }
    },
    [link, navigate]
  );
  const uniqueId = targetRef || `tooltip-${uuidv4()}`;
  const [tooltipWidth, setTooltipWidth] = useState<number | undefined>(undefined);
  const textRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const el = textRef.current;
      if (!el) {
        return;
      }
      const textWidth = el.offsetWidth;
      setTooltipWidth(Math.min(textWidth + 2, 200));
    });

    return () => cancelAnimationFrame(id);
  }, [tooltipText, t]);

  if (!tooltipText) {
    return null;
  }

  return (
    <>
      <span
        ref={textRef}
        style={{
          visibility: 'hidden',
          left: '-2000px',
          position: 'absolute',
          fontSize: '10px',
          fontWeight: '500',
          fontFamily: 'Inter',
        }}
      >
        {link && `${t(linkLabel || 'upgrade')} `}
        {t(tooltipText)}
      </span>
      {!targetRef && (
        <span
          id={uniqueId}
          className={`${iconClass} ico--xs ${addMargin ? 'ico--info-margin' : ''} ${additionalIcoClass}`}
        />
      )}
      {link ? (
        <Tooltip
          target={`#${uniqueId}`}
          className={`${direction ? `tooltip--${direction}` : ''} ${
            isMobileTooltip ? 'tooltip--mobile' : ''
          }`}
          position={direction}
          style={{ width: tooltipWidth }}
          autoHide={false}
          hideDelay={50}
        >
          <span>
            <Button onClick={handleLinkClick} className="btn--link ">
              {t(linkLabel || 'upgrade')}
            </Button>{' '}
            {t(tooltipText)}
          </span>
        </Tooltip>
      ) : (
        <Tooltip
          target={`#${uniqueId}`}
          content={t(tooltipText)}
          className={`${direction ? `tooltip--${direction}` : ''} ${
            isMobileTooltip ? 'tooltip--mobile' : ''
          }`}
          position={direction}
          style={{ width: tooltipWidth }}
          showDelay={5}
        />
      )}
    </>
  );
};

export default CustomTooltip;
